import { Description, OpenInNew } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import server from '../../../services/server';
import { getStoredAdvertiser, getStoredUser, persistSessionAdvertiser } from '../../../services/authSession';
import useErrors from '../../../hooks/useErrors';
import { useFetch } from '../../../hooks/useFecth';
import { useAdvertiserContractSocket } from '../../../hooks/useAdvertiserContractSocket';

const digitsOnly = value => String(value ?? '').replace(/\D/g, '');

function isValidCpf(value) {
    const cpf = digitsOnly(value);
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
    const digit = weights => {
        const sum = weights.reduce((total, weight, index) => total + Number(cpf[index]) * weight, 0);
        const mod = sum % 11;
        return mod < 2 ? 0 : 11 - mod;
    };
    return digit([10, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(cpf[9]) && digit([11, 10, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(cpf[10]);
}

const responsibleContractSchema = yup.object().shape({
    responsibleName: yup.string().trim().required('Informe o nome do responsável'),
    responsibleEmail: yup.string().trim().email('E-mail inválido').required('Informe o e-mail do responsável'),
    responsiblePhone: yup
        .string()
        .trim()
        .required('Informe o telefone do responsável')
        .test('phone', 'Informe um telefone válido', v => digitsOnly(v).length >= 10),
    responsibleDocument: yup
        .string()
        .trim()
        .required('Informe o CPF do responsável')
        .test('cpf', 'Informe um CPF válido', isValidCpf),
});

function isContractSigned(contract) {
    return Boolean(contract?.signedAt) || ['SIGNED', 'CONCLUDED'].includes(contract?.status);
}

function contractFromResponse(response) {
    return response?.data?.data || null;
}

export default function AdvertiserContract() {
    const [activeStep, setActiveStep] = useState(0);
    const [contract, setContract] = useState(null);
    const [sending, setSending] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const bootstrapped = useRef(false);
    const storedAdvertiser = getStoredAdvertiser();
    const advertiserId = storedAdvertiser?.id || getStoredUser()?.advertiserId;
    const { data: profile, mutate } = useFetch('/advertiser/profile');
    const { setErrors } = useErrors();
    const navigate = useNavigate();
    const { connected } = useAdvertiserContractSocket(advertiserId, {
        enabled: activeStep === 2 && !isContractSigned(contract),
        onUpdate: () => {
            mutate();
        },
    });

    const hasContractSigned = isContractSigned(contract) || isContractSigned(profile?.contract);
    const previewHtml = contract?.contractData?.contractText || '';
    const document = contract?.contractData?.document;
    const signer = (document?.signers || []).find(item => item?.sign_url || item?.url || item?.short_url);
    const zapSignUrl = contract?.signUrl || signer?.sign_url || signer?.url || signer?.short_url || '';
    const originalPdfUrl = contract?.Uploads?.find(item => item.description === 'contrato_original')?.fileUrl;
    const signedPdfUrl = contract?.Uploads?.find(item => item.description === 'contrato_assinado')?.fileUrl;

    useEffect(() => {
        if (bootstrapped.current || !profile) return;
        bootstrapped.current = true;
        const row = profile.contract;
        if (!row?.id) return;
        setContract(row);
        if (isContractSigned(row) || row.integrationId) setActiveStep(2);
        else if (row.contractData?.contractText) setActiveStep(1);
    }, [profile]);

    useEffect(() => {
        if (!isContractSigned(profile?.contract)) return;
        setContract(profile.contract);
        setActiveStep(2);
    }, [profile?.contract]);

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: responsibleContractSchema,
        initialValues: {
            responsibleName: profile?.responsibleName || '',
            responsibleEmail: profile?.responsibleEmail || '',
            responsiblePhone: profile?.responsiblePhone || '',
            responsibleDocument: profile?.responsibleDocument || '',
        },
        onSubmit: async values => {
            try {
                await server.put('/advertiser/profile', {
                    responsibleName: values.responsibleName.trim(),
                    responsibleEmail: values.responsibleEmail.trim(),
                    responsiblePhone: values.responsiblePhone.trim(),
                    responsibleDocument: values.responsibleDocument.trim(),
                });
                await mutate();

                const response = await server.post('/advertiser/contract/preview');
                const next = contractFromResponse(response);
                setContract(next);
                setActiveStep(next?.integrationId || isContractSigned(next) ? 2 : 1);
            } catch (error) {
                setErrors(error);
            }
        },
    });

    const handleSend = async () => {
        try {
            setSending(true);
            const response = await server.post('/advertiser/contract/send');
            const next = contractFromResponse(response);
            setContract(next);
            setActiveStep(2);
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setSending(false);
        }
    };

    const handleSync = async () => {
        try {
            setSyncing(true);
            const response = await server.post('/advertiser/contract/sync');
            const next = contractFromResponse(response);
            setContract(next);
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setSyncing(false);
        }
    };

    const handleProceed = () => {
        const current = getStoredAdvertiser();
        if (current) {
            persistSessionAdvertiser({
                ...current,
                contractId: contract?.id || profile?.contract?.id || current.contractId,
                contractSigned: true,
            });
        }
        navigate('/anunciante', { replace: true });
    };

    return (
        <Stack spacing={3}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Assinatura de contrato
                    </Typography>
                    <Typography color="text.secondary" mt={1}>
                        Confira os dados, leia o contrato e assine no ZapSign.
                    </Typography>
                </Box>

                <Card variant="outlined">
                    <CardContent>
                        <Stack spacing={3}>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                <Step>
                                    <StepLabel>Confirmar dados</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Ler contrato</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Assinar</StepLabel>
                                </Step>
                            </Stepper>

                            {activeStep === 0 && (
                                <Stack component="form" spacing={2} onSubmit={formik.handleSubmit} noValidate>
                                    <Typography variant="h6">Etapa 1: Confirmação de dados</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Confira o responsável e o CPF. Se voltar da leitura do contrato, altere o que precisar e gere o texto de novo.
                                    </Typography>

                                    <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                                        <CardContent>
                                            <Stack spacing={1.25}>
                                                <Typography variant="body2">
                                                    <strong>Empresa: </strong>
                                                    {profile?.name || 'Não informado'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Documento: </strong>
                                                    {profile?.document || 'Não informado'}
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>

                                    <Card variant="outlined">
                                        <CardContent>
                                            <Stack spacing={2}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    Dados do responsável
                                                </Typography>
                                                    <TextField
                                                        fullWidth
                                                        label="Responsável"
                                                        name="responsibleName"
                                                        value={formik.values.responsibleName}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        error={formik.touched.responsibleName && Boolean(formik.errors.responsibleName)}
                                                        helperText={formik.touched.responsibleName && formik.errors.responsibleName}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label="E-mail do responsável"
                                                        name="responsibleEmail"
                                                        type="email"
                                                        value={formik.values.responsibleEmail}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        error={formik.touched.responsibleEmail && Boolean(formik.errors.responsibleEmail)}
                                                        helperText={formik.touched.responsibleEmail && formik.errors.responsibleEmail}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label="Telefone do responsável"
                                                        name="responsiblePhone"
                                                        value={formik.values.responsiblePhone}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        error={formik.touched.responsiblePhone && Boolean(formik.errors.responsiblePhone)}
                                                        helperText={formik.touched.responsiblePhone && formik.errors.responsiblePhone}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label="CPF do responsável"
                                                        name="responsibleDocument"
                                                        value={formik.values.responsibleDocument}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        error={
                                                            formik.touched.responsibleDocument &&
                                                            Boolean(formik.errors.responsibleDocument)
                                                        }
                                                        helperText={formik.touched.responsibleDocument && formik.errors.responsibleDocument}
                                                    />
                                                </Stack>
                                            </CardContent>
                                        </Card>

                                    <Button type="submit" variant="contained" startIcon={<Description />} disabled={formik.isSubmitting}>
                                        Salvar e ver contrato
                                    </Button>
                                </Stack>
                            )}

                            {activeStep === 1 && (
                                <Stack spacing={2}>
                                    <Typography variant="h6">Etapa 2: Leitura do contrato</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Este é o texto que será enviado para assinatura. Se precisar corrigir algum dado, volte antes de confirmar.
                                    </Typography>
                                    <Box
                                        sx={{
                                            maxHeight: 420,
                                            overflow: 'auto',
                                            p: 2,
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            bgcolor: 'grey.50',
                                            '& p': { fontSize: '0.9rem', lineHeight: 1.55 },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                    />
                                    <Stack direction="row" spacing={1.5} justifyContent="space-between">
                                        <Button type="button" color="inherit" onClick={() => setActiveStep(0)}>
                                            Voltar
                                        </Button>
                                        <Button type="button" variant="contained" disabled={sending || !previewHtml} onClick={handleSend}>
                                            Confirmar e gerar contrato
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}

                            {activeStep === 2 && (
                                <Stack spacing={2}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                        <Typography variant="h6">Etapa 3: Assinatura</Typography>
                                        <Chip
                                            size="small"
                                            color={hasContractSigned ? 'success' : 'warning'}
                                            label={hasContractSigned ? 'Assinado' : 'Aguardando assinatura'}
                                        />
                                    </Stack>

                                    {hasContractSigned ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Contrato assinado. Você já pode entrar na área do anunciante.
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Enviamos o link também por e-mail. Se não chegar, abra o ZapSign por aqui.
                                            {connected
                                                ? ' Esta tela acompanha a assinatura.'
                                                : ' Se não atualizar sozinha, use Já assinei.'}
                                        </Typography>
                                    )}

                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                        {!hasContractSigned && (
                                            <Button
                                                variant="contained"
                                                startIcon={<OpenInNew />}
                                                disabled={!zapSignUrl}
                                                component="a"
                                                href={zapSignUrl || undefined}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Abrir ZapSign
                                            </Button>
                                        )}
                                        {(signedPdfUrl || originalPdfUrl) && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<OpenInNew />}
                                                component="a"
                                                href={hasContractSigned ? signedPdfUrl || originalPdfUrl : originalPdfUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {hasContractSigned ? 'Ver contrato assinado' : 'Ver PDF'}
                                            </Button>
                                        )}
                                    </Stack>

                                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                        {!hasContractSigned ? (
                                            <Button type="button" variant="outlined" disabled={syncing} onClick={handleSync}>
                                                Já assinei
                                            </Button>
                                        ) : (
                                            <Button type="button" variant="contained" onClick={handleProceed}>
                                                Prosseguir
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            )}

                            <Divider />
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
    );
}
